/**
 * Compass View Spec v1 — visningen Spør Compass bygger for et svar, og som lagres i
 * SavedReport.ChartConfig.view (ingen skjemaendring). Speiler backend-validatoren
 * BizClick.WMS.Core/Services/CompassQuery/ViewSpecValidator.cs.
 *
 * Data ligger aldri i visningen: panelene peker på datasett ved id, og radene bindes ved visning.
 */
import type { ColumnDefinition } from '../../../../../services/claudeQueryService';

export type PanelKind = 'kpi' | 'table' | 'chart' | 'vega-lite' | 'text' | 'heatmap' | 'funnel' | 'treemap';
export type ChartType = 'bar' | 'line' | 'area' | 'pie' | 'stacked-bar' | 'horizontal-bar' | 'gauge';
export type SemanticColor = 'good' | 'bad' | 'warning' | 'neutral' | 'accent';

export interface ViewDataset {
    id: string;
    purpose?: string;
    /** SQL for ekstra datasett. Det primære datasettet (indeks 0) bruker rapportens GeneratedSQL. */
    sql?: string | null;
}

export interface LayoutItem {
    panel: string;
    x: number;
    y: number;
    w: number;
    h: number;
}

export type TransformStep =
    | { filter: { field: string; op: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'notnull'; value?: unknown } }
    | { aggregate: { groupBy?: string[]; measures: { field?: string; op: 'sum' | 'avg' | 'count' | 'min' | 'max' | 'distinct'; as: string }[] } }
    | { sort: { field: string; dir?: 'asc' | 'desc' }[] }
    | { limit: number }
    | { topN: { n: number; field: string; labelField: string; othersLabel?: string } }
    | { derive: { as: string; expr: string } };

export interface KpiItemSpec {
    label: string;
    field?: string;
    aggregate?: 'sum' | 'avg' | 'count' | 'min' | 'max' | 'first' | 'last' | 'distinct';
    format?: 'integer' | 'decimal1' | 'decimal2' | 'percent' | 'currency' | 'duration_s' | 'text';
    thresholds?: { good?: string; bad?: string };
    dataset?: string;
}

export interface ViewPanel {
    id: string;
    kind: PanelKind;
    title?: string;
    subtitle?: string;
    dataset?: string;
    transform?: TransformStep[];
    /** Native paneler */
    config?: Record<string, any>;
    /** Vega-Lite (uten data) */
    spec?: Record<string, any>;
    /** Text-panel (markdown) */
    content?: string;
}

export interface ViewSpec {
    specVersion: 1;
    title: string;
    description?: string;
    createdBy?: 'compass' | 'user';
    datasets: ViewDataset[];
    layout: { columns: number; items: LayoutItem[] };
    panels: ViewPanel[];
}

/** Rader for ett datasett, slik visningen får dem (fra chat-svar eller rapportkjøring). */
export interface DatasetPayload {
    id: string;
    purpose?: string;
    sql?: string;
    columns: ColumnDefinition[];
    rows: Record<string, any>[];
    rowCount?: number;
    truncated?: boolean;
}

export type DatasetMap = Record<string, DatasetPayload>;

export function isViewSpec(value: unknown): value is ViewSpec {
    const v = value as ViewSpec | null;
    return !!v && typeof v === 'object' && v.specVersion === 1 && Array.isArray(v.panels) && !!v.layout && Array.isArray(v.layout.items);
}

/** Leser visningen fra SavedReport.ChartConfig (JSON-streng), hvis den finnes. */
export function viewFromChartConfig(chartConfig?: string | null): ViewSpec | null {
    if (!chartConfig) return null;
    try {
        const parsed = JSON.parse(chartConfig);
        return isViewSpec(parsed?.view) ? parsed.view : null;
    } catch {
        return null;
    }
}

/** Kort oppsummering av visningen til samtalehistorikken (gir presise oppfølgingsspørsmål). */
export function summarizeView(view: ViewSpec, datasets?: DatasetMap): string {
    const panels = view.panels.map(p => `${p.kind}${p.config?.chartType ? '/' + p.config.chartType : ''} «${p.title ?? p.id}»`).join(', ');
    const ds = Object.values(datasets ?? {}).map(d => `${d.id}: ${d.rowCount ?? d.rows.length} rader, kolonner ${d.columns.map(c => c.name).join(', ')}`).join('; ');
    return `«${view.title}» — paneler: ${panels}${ds ? `. Datasett: ${ds}` : ''}`;
}
